import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { Input } from '@/shared/ui/inputs/Input';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { usePackagesParcelPicker } from '@/entities/packages';
import { formatDate } from '@/shared/utils/date';
import { formatDecimalTrim } from '@/shared/utils/currency';
import type { ApiPackagesParcelPickerItem } from '@/shared/types/api/packages';

type Props = {
  /** Currently attached parcels (form state). */
  value: ApiPackagesParcelPickerItem[];
  /** Replace the list with a new one. */
  onChange: (next: ApiPackagesParcelPickerItem[]) => void;
  /** Show inline validation error (e.g. "at least one required"). */
  error?: string;
};

const MAX_RESULTS = 50;

const sumQuantities = (products: ApiPackagesParcelPickerItem['products']): number =>
  products.reduce((acc, p) => acc + (Number(p.quantity) || 0), 0);

const sumTotal = (products: ApiPackagesParcelPickerItem['products']): number =>
  products.reduce(
    (acc, p) => acc + (Number(p.quantity) || 0) * (Number(p.price_per_item) || 0),
    0,
  );

const formatMoney = (n: number): string => {
  if (!Number.isFinite(n)) return '0';
  const fixed = formatDecimalTrim(n);
  return fixed;
};

const ciIncludes = (haystack: string | null | undefined, needle: string): boolean => {
  if (!needle) return true;
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
};

export const FeaturesPackagesParcelPicker = ({ value, onChange, error }: Props) => {
  const { t } = useTranslation();
  const [trekQuery, setTrekQuery] = useState('');
  const [nameQuery, setNameQuery] = useState('');
  const [cargoQuery, setCargoQuery] = useState('');

  const debouncedTrek = useDebouncedValue(trekQuery, 250);
  const debouncedName = useDebouncedValue(nameQuery, 250);
  const debouncedCargo = useDebouncedValue(cargoQuery, 250);

  // Picker always enabled — inline layout, no modal gating.
  const query = usePackagesParcelPicker({}, true);

  const selectedIds = useMemo(() => new Set(value.map((p) => p.id)), [value]);

  const handleAdd = (parcel: ApiPackagesParcelPickerItem): void => {
    if (selectedIds.has(parcel.id)) return;
    onChange([...value, parcel]);
  };

  const handleRemove = (id: string): void => {
    onChange(value.filter((p) => p.id !== id));
  };

  const trek = debouncedTrek.trim();
  const name = debouncedName.trim();
  const cargo = debouncedCargo.trim();

  const filteredAvailable = useMemo<ApiPackagesParcelPickerItem[]>(() => {
    if (!query.data) return [];
    const list = query.data.filter((row) => {
      if (selectedIds.has(row.id)) return false;
      if (trek && !ciIncludes(row.trek_id, trek)) return false;
      if (name) {
        const nameMatch =
          ciIncludes(row.name, name) ||
          row.products.some((p) => ciIncludes(p.full_name, name));
        if (!nameMatch) return false;
      }
      if (cargo && !ciIncludes(row.cargo_id_code, cargo)) return false;
      return true;
    });
    return list.slice(0, MAX_RESULTS);
  }, [query.data, selectedIds, trek, name, cargo]);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">
          {t('packages.parcelPicker.title')}
        </h3>
        <p className="text-xs text-ink-muted">
          {t('packages.parcelPicker.subtitle', { count: value.length })}
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
          {error}
        </p>
      )}

      {/* TOP ROW — selected parcels */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
        {value.length === 0 ? (
          <p className="px-4 py-3 text-sm text-ink-muted">
            {t('packages.parcelPicker.selectedEmpty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    #
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.trekId')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.productName')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.cargoId')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.quantity')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.totalPrice')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.date')}
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    {t('packages.columns.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {value.map((parcel, idx) => {
                  const firstProduct = parcel.products[0];
                  const qty = sumQuantities(parcel.products);
                  const total = sumTotal(parcel.products);
                  return (
                    <tr
                      key={parcel.id}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="px-3 py-2.5 font-mono text-xs text-ink-faint">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-ink-soft">
                        {parcel.trek_id}
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">
                        <span className="block max-w-[220px] truncate font-medium text-ink">
                          {firstProduct?.full_name ?? parcel.name ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-ink-soft">
                        {parcel.cargo_id_code ?? '—'}
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">{qty || '—'}</td>
                      <td className="px-3 py-2.5 font-medium text-ink">
                        {formatMoney(total)}
                      </td>
                      <td className="px-3 py-2.5 text-ink-soft">
                        {formatDate(parcel.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <IconButton
                          label={t('common.delete')}
                          variant="danger"
                          onClick={() => handleRemove(parcel.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </IconButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BOTTOM ROW — picker */}
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-card">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Input
            value={trekQuery}
            onChange={(e) => setTrekQuery(e.target.value.toUpperCase())}
            placeholder={t('packages.parcelPicker.filters.trekId')}
            aria-label={t('packages.parcelPicker.filters.trekId')}
            autoCapitalize="characters"
            maxLength={255}
          />
          <Input
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder={t('packages.parcelPicker.filters.name')}
            aria-label={t('packages.parcelPicker.filters.name')}
          />
          <Input
            value={cargoQuery}
            onChange={(e) => setCargoQuery(e.target.value.toUpperCase())}
            placeholder={t('packages.parcelPicker.filters.cargoId')}
            aria-label={t('packages.parcelPicker.filters.cargoId')}
            autoCapitalize="characters"
            inputMode="text"
            maxLength={255}
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {query.isLoading ? (
            <div className="flex min-h-[160px] items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : query.error ? (
            <div className="p-4">
              <ErrorState
                message={extractErrorMessage(
                  query.error,
                  t('packages.parcelPicker.dialog.loadError'),
                )}
                onRetry={() => void query.refetch()}
              />
            </div>
          ) : filteredAvailable.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title={t('packages.parcelPicker.dialog.emptyTitle')}
                description={t('packages.parcelPicker.dialog.emptyDescription')}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.parcelPicker.dialog.addRow')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.trekId')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.productName')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.cargoId')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.quantity')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.totalPrice')}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                      {t('packages.columns.date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAvailable.map((row) => {
                    const first = row.products[0];
                    const qty = sumQuantities(row.products);
                    const total = sumTotal(row.products);
                    return (
                      <tr
                        key={row.id}
                        className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            leftIcon={<Plus className="h-4 w-4" />}
                            onClick={() => handleAdd(row)}
                          >
                            {t('packages.parcelPicker.dialog.addRow')}
                          </Button>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-ink-soft">
                          {row.trek_id}
                        </td>
                        <td className="px-3 py-2.5 text-ink-soft">
                          <span className="block max-w-[220px] truncate font-medium text-ink">
                            {first?.full_name ?? row.name ?? '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs text-ink-soft">
                          {row.cargo_id_code ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-ink-soft">{qty || '—'}</td>
                        <td className="px-3 py-2.5 font-medium text-ink">
                          {formatMoney(total)}
                        </td>
                        <td className="px-3 py-2.5 text-ink-soft">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
